// app/javascript/three/toolboxes_panel.js

export function createToolboxesPanel({
  mountEl,
  toolboxes = [],
  topbarId = "main-topbar",
  gapPx = 12,
  onToolClick = null,
} = {}) {
  if (!mountEl) return null;

  const normalizedToolboxes = Array.isArray(toolboxes) ? toolboxes : [];

  const state = {
    openId: normalizedToolboxes[0]?.id ?? null,
    toolboxes: normalizedToolboxes,
  };

  function el(tag, className = "", attrs = {}) {
    const node = document.createElement(tag);
    if (className) node.className = className;

    Object.entries(attrs).forEach(([k, v]) => {
      if (v === null || v === undefined) return;
      node.setAttribute(k, String(v));
    });

    return node;
  }

  function applyTopOffset() {
    const topbar = document.getElementById(topbarId);
    const topOffset = topbar ? topbar.offsetHeight + gapPx : gapPx;

    mountEl.style.top = `${topOffset}px`;
    mountEl.style.maxHeight = `calc(100vh - ${topOffset + gapPx}px)`;
  }

  function applyMountStyle() {
    mountEl.classList.add("pano-toolboxes");
  }

  function buildToolTile(t) {
    const isDraggable = !!t.is_draggable;
    const isClickable = !!t.is_clickable;

    const tile = el("button", "pano-toolbox__tool", {
      type: "button",
      title: t.name || "",
      "data-tool-id": t.id,
      "data-draggable": isDraggable ? "true" : "false",
      "data-clickable": isClickable ? "true" : "false",
      draggable: "false",
    });

    if (isDraggable) {
      tile.classList.add("is-draggable");
    } else if (isClickable) {
      tile.classList.add("is-clickable");
    } else {
      tile.classList.add("is-disabled");
    }

    if (isClickable) {
      tile.addEventListener("click", () => {
        if (typeof onToolClick === "function") onToolClick(t);
      });
    }

    if (t.image_url) {
      const img = el("img", "pano-toolbox__tool-image");
      img.src = t.image_url;
      img.alt = t.name || "";
      img.loading = "lazy";
      img.draggable = false;
      tile.appendChild(img);
    } else {
      const ph = el("div", "pano-toolbox__tool-placeholder");
      ph.textContent = t.name || "Tool";
      tile.appendChild(ph);
    }

    return tile;
  }

  function buildToolCard(tb) {
    const wrap = el("div", "pano-toolbox__card");

    const headerBtn = el("button", "pano-toolbox__header", { type: "button" });

    const titleWrap = el("div", "pano-toolbox__header-main");

    const title = el("div", "pano-toolbox__title");
    title.textContent = tb.name || `Toolbox #${tb.id}`;

    titleWrap.appendChild(title);

    const chevron = el("div", "pano-toolbox__chevron");
    chevron.textContent = "▸";

    headerBtn.appendChild(titleWrap);
    headerBtn.appendChild(chevron);

    const body = el("div", "pano-toolbox__body");

    const list = el("div", "pano-toolbox__grid");
    (tb.tools || []).forEach((t) => {
      list.appendChild(buildToolTile(t));
    });

    if ((tb.tools || []).length === 0) {
      const empty = el("div", "pano-toolbox__empty");
      empty.textContent = "Nessun tool in questa toolbox.";
      body.appendChild(empty);
    } else {
      body.appendChild(list);
    }

    wrap.appendChild(headerBtn);
    wrap.appendChild(body);

    function setOpen(isOpen) {
      wrap.classList.toggle("is-open", isOpen);
      body.style.display = isOpen ? "block" : "none";
      chevron.textContent = isOpen ? "▾" : "▸";
    }

    headerBtn.addEventListener("click", () => {
      state.openId = state.openId === tb.id ? null : tb.id;
      render();
    });

    return { wrap, setOpen, id: tb.id };
  }

  function render() {
    mountEl.innerHTML = "";

    if (!state.toolboxes.length) {
      mountEl.classList.add("hidden");
      return;
    }

    mountEl.classList.remove("hidden");
    applyMountStyle();

    const cards = state.toolboxes.map(buildToolCard);
    cards.forEach((card) => {
      card.setOpen(state.openId === card.id);
      mountEl.appendChild(card.wrap);
    });
  }

  function setToolboxes(toolboxes) {
    state.toolboxes = Array.isArray(toolboxes) ? toolboxes : [];
    state.openId = state.toolboxes[0]?.id ?? null;
    render();
    applyTopOffset();
  }

  applyTopOffset();
  applyMountStyle();
  window.addEventListener("resize", applyTopOffset);
  render();

  return {
    setToolboxes,
    destroy() {
      window.removeEventListener("resize", applyTopOffset);
      mountEl.innerHTML = "";
      mountEl.style.top = "";
      mountEl.style.maxHeight = "";
      mountEl.classList.remove("pano-toolboxes");
    },
  };
}